import { Stack } from 'braid-design-system';
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { useMelwaysLink } from '@seek/melways-react';

import {
  AwardsSection,
  CompanyOverviewSection,
  FeaturedSection,
  StatementSection,
  GallerySection,
  useAppContext,
} from '@seek/libs-shared';

import type { AboutModel } from './aboutModel';
import { useConfig } from '../../../../../shared/src/providers/configProvider';
import { CompanyProfilesPage, ROOT_PATH } from '../../paths';
import { trackViewEvents } from '../../../shared/trackViewEvents';
import { ReviewsSummarySection } from '../../../companyReviews/reviewsSummarySection/ReviewsSummarySection';

interface AboutPageProps {
  model: AboutModel;
  setActiveTab?: Dispatch<SetStateAction<string | undefined>>;
  trackWriteAReviewClicked: () => void;
  trackReviewsClicked: () => void;
  trackSeeAllPhotosButtonClicked: () => void;
  trackWebsiteClicked: () => void;
}

export function AboutPage({
  model,
  setActiveTab,
  trackWriteAReviewClicked,
  trackReviewsClicked,
  trackSeeAllPhotosButtonClicked,
  trackWebsiteClicked,
}: AboutPageProps) {
  const config = useConfig();
  const { language } = config;
  const context = useAppContext();
  const urlParams = useParams();
  const localize = useMelwaysLink();

  useEffect(() => {
    trackViewEvents.aboutPage({ config, context });
  });

  const shouldDisplayOverviewSection = Boolean(
    model.overview &&
      Object.keys(model.overview).length > 0 &&
      Object.values(model.overview).some(Boolean),
  );
  const shouldDisplayFeaturedSection = Boolean(
    model.featured && Object.keys(model.featured).length > 0,
  );
  const shouldDisplayAwardsSection = Boolean(
    model.awards && Object.keys(model.awards).length > 0,
  );

  return (
    <Stack space={['none', 'gutter']}>
      {shouldDisplayOverviewSection && (
        <CompanyOverviewSection
          mode="view"
          model={{ ...model.overview, name: model.name }}
          trackWebsiteClicked={trackWebsiteClicked}
        />
      )}
      {model.missionStatement && model.missionStatement.length > 0 ? (
        <StatementSection
          mode="view"
          model={{ description: model.missionStatement }}
          isMissionStatement
        />
      ) : null}
      {model.reviewSummary && (
        <ReviewsSummarySection
          model={{ ...model.reviewSummary, name: model.name }}
          isOnAboutPage
          setActiveTab={setActiveTab}
          trackWriteAReviewClicked={trackWriteAReviewClicked}
          trackReviewsClicked={trackReviewsClicked}
        />
      )}
      {shouldDisplayFeaturedSection ? (
        <FeaturedSection mode="view" model={model.featured} />
      ) : null}
      {shouldDisplayAwardsSection ? (
        <AwardsSection mode="view" model={model.awards} />
      ) : null}
      {model.gallery && model.gallery.photos.length > 0 ? (
        <GallerySection
          mode="view"
          model={model.gallery}
          link={localize({
            path: `/${ROOT_PATH}/${urlParams.companySlug}/${CompanyProfilesPage.Culture}`,
            language,
          })}
          setActiveTab={setActiveTab}
          trackSeeAllPhotosButtonClicked={trackSeeAllPhotosButtonClicked}
        />
      ) : null}
    </Stack>
  );
}
